import React, { useState, useEffect } from 'react';
import { getAllAdminUsers, deactivateAdminUser, reactivateAdminUser } from '../../Api';
import { EyeIcon, PencilIcon, TrashIcon, UserPlusIcon, ArrowPathIcon, XCircleIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import { ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import AdminAuditTrailModal from './AdminAuditTrailModal';
import Pagination from '../../Pagination';

const AdminUserList = ({ onEditUser, onViewDetails }) => {
  const [adminUsers, setAdminUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedAdminId, setSelectedAdminId] = useState(null);
  const [showAuditTrailModal, setShowAuditTrailModal] = useState(false);
  const [showDeactivateConfirmation, setShowDeactivateConfirmation] = useState(false);
  const [adminToDeactivate, setAdminToDeactivate] = useState(null);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  useEffect(() => {
    fetchAdminUsers();
  }, []);

  const fetchAdminUsers = async () => {
    try {
      setLoading(true);
      const response = await getAllAdminUsers();
      setAdminUsers(response || []);
      setError(null);
    } catch (err) {
      setError('Failed to load admin users');
      console.error('Error fetching admin users:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeactivate = async (adminId) => {
    setAdminToDeactivate(adminId);
    setShowDeactivateConfirmation(true);
  };

  const confirmDeactivate = async () => {
    try {
      await deactivateAdminUser(adminToDeactivate);
      fetchAdminUsers(); // Refresh the list
      setShowDeactivateConfirmation(false);
      setAdminToDeactivate(null);
    } catch (err) {
      setError('Failed to deactivate admin user');
      console.error('Error deactivating admin user:', err);
    }
  };

  const handleReactivate = async (adminId) => {
    try {
      await reactivateAdminUser(adminId);
      fetchAdminUsers(); // Refresh the list
    } catch (err) {
      setError('Failed to reactivate admin user');
      console.error('Error reactivating admin user:', err);
    }
  };

  const handleViewAuditTrails = (adminId) => {
    setSelectedAdminId(adminId);
    setShowAuditTrailModal(true);
  };

  // Filter and search logic
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const renderSortIcon = (key) => {
    if (sortConfig.key !== key) return <ArrowUpDown className="w-4 h-4 ml-1 opacity-30 inline-block" />;
    return sortConfig.direction === 'asc' ?
      <ArrowUp className="w-4 h-4 ml-1 text-blue-600 inline-block" /> :
      <ArrowDown className="w-4 h-4 ml-1 text-blue-600 inline-block" />;
  };

  const filteredUsers = adminUsers.filter(user => {
    const matchesSearch = user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.lastName.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = filterRole === 'all' || user.roleName?.toLowerCase() === filterRole.toLowerCase();
    return matchesSearch && matchesRole;
  }).sort((a, b) => {
    if (!sortConfig.key) return 0;

    let aVal = a[sortConfig.key];
    let bVal = b[sortConfig.key];

    if (sortConfig.key === 'name') {
      aVal = ((a.firstName || '') + ' ' + (a.lastName || '')).toLowerCase();
      bVal = ((b.firstName || '') + ' ' + (b.lastName || '')).toLowerCase();
    } else if (typeof aVal === 'string') {
      aVal = aVal.toLowerCase();
      bVal = bVal.toLowerCase();
    }

    if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
    return 0;
  });

  // Pagination
  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedUsers = filteredUsers.slice(startIndex, startIndex + itemsPerPage);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <ArrowPathIcon className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-4">
        <p className="text-red-700">{error}</p>
        <button
          onClick={fetchAdminUsers}
          className="mt-2 px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Admin Users</h2>
          <p className="text-sm text-gray-500 mt-1">
            Manage platform administrators and their permissions
          </p>
        </div>
        <button
          onClick={() => onEditUser(null)}
          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 transition-colors"
        >
          <UserPlusIcon className="h-4 w-4 mr-2" />
          Add Admin
        </button>
      </div>

      {/* Filters */}
      <div className="p-6 border-b border-gray-200 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-1">
              Search
            </label>
            <input
              type="text"
              id="search"
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900"
            />
          </div>
          <div>
            <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-1">
              Filter by Role
            </label>
            <select
              id="role"
              value={filterRole}
              onChange={(e) => {
                setFilterRole(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900"
            >
              <option value="all">All Roles</option>
              <option value="admin">Admin</option>
              <option value="super_admin">Super Admin</option>
            </select>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                S.No
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors group"
                onClick={() => handleSort('name')}
              >
                <div className="flex items-center">
                  User {renderSortIcon('name')}
                </div>
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors group"
                onClick={() => handleSort('email')}
              >
                <div className="flex items-center">
                  Email {renderSortIcon('email')}
                </div>
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors group"
                onClick={() => handleSort('roleName')}
              >
                <div className="flex items-center">
                  Role {renderSortIcon('roleName')}
                </div>
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors group"
                onClick={() => handleSort('active')}
              >
                <div className="flex items-center">
                  Status {renderSortIcon('active')}
                </div>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {paginatedUsers.map((user, index) => (
              <tr key={user.adminId} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-mono">
                  {(currentPage - 1) * itemsPerPage + index + 1}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 h-10 w-10">
                      <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                        <span className="text-blue-600 font-medium">
                          {user.firstName.charAt(0)}
                          {user.lastName.charAt(0)}
                        </span>
                      </div>
                    </div>
                    <div className="ml-4">
                      <div className="text-sm font-medium text-gray-900">
                        {user.firstName} {user.lastName}
                      </div>
                      <div className="text-sm text-gray-500">
                        Created: {new Date(user.createdAt).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">{user.email}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${user.roleName.toLowerCase() === 'super_admin'
                      ? 'bg-purple-100 text-purple-800'
                      : 'bg-blue-100 text-blue-800'
                    }`}>
                    {user.roleName.replace('_', ' ').toUpperCase()}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${user.status
                      ? 'bg-green-100 text-green-800'
                      : 'bg-red-100 text-red-800'
                    }`}>
                    {user.status ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                  <button
                    onClick={() => onViewDetails(user.adminId)}
                    className="text-blue-600 hover:text-blue-900 transition-colors"
                    title="View Details"
                  >
                    <EyeIcon className="h-4 w-4" />
                  </button>
                  {user.roleName !== 'SUPER_ADMIN' && (
                    <button
                      onClick={() => handleViewAuditTrails(user.adminId)}
                      className="text-indigo-600 hover:text-indigo-900 transition-colors"
                      title="View Audit Trails"
                    >
                      <DocumentTextIcon className="h-4 w-4" />
                    </button>
                  )}
                  {user.roleName !== 'SUPER_ADMIN' && (
                    <>
                      <button
                        onClick={() => onEditUser(user)}
                        className="text-indigo-600 hover:text-indigo-900 transition-colors"
                        title="Edit User"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      {user.status ? (
                        <button
                          onClick={() => handleDeactivate(user.adminId)}
                          className="text-red-600 hover:text-red-900 transition-colors"
                          title="Deactivate User"
                        >
                          <XCircleIcon className="h-4 w-4" />
                        </button>
                      ) : (
                        <button
                          onClick={() => handleReactivate(user.adminId)}
                          className="text-green-600 hover:text-green-900 transition-colors"
                          title="Reactivate User"
                        >
                          <ArrowPathIcon className="h-4 w-4" />
                        </button>
                      )}
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <Pagination
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        totalPages={totalPages}
        totalElements={filteredUsers.length}
        pageSize={itemsPerPage}
        setPageSize={setItemsPerPage}
        itemName="Admin Users"
      />

      {paginatedUsers.length === 0 && (
        <div className="text-center py-12">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"
            />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">No admin users found</h3>
          <p className="mt-1 text-sm text-gray-500">
            {searchTerm || filterRole !== 'all' ? 'Try adjusting your search or filter criteria.' : 'Get started by adding a new admin user.'}
          </p>
          <div className="mt-6">
            <button
              onClick={() => onEditUser(null)}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 transition-colors"
            >
              <UserPlusIcon className="h-4 w-4 mr-2" />
              Add Admin User
            </button>
          </div>
        </div>
      )}

      {showAuditTrailModal && (
        <AdminAuditTrailModal
          adminId={selectedAdminId}
          onClose={() => setShowAuditTrailModal(false)}
        />
      )}

      {showDeactivateConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Confirm Deactivation</h3>
              <button
                onClick={() => {
                  setShowDeactivateConfirmation(false);
                  setAdminToDeactivate(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="mb-6">
              <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4 mb-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-yellow-800">Important Notice</h3>
                    <div className="mt-2 text-sm text-yellow-700">
                      <p>Deactivating an admin user will:</p>
                      <ul className="list-disc pl-5 mt-1 space-y-1">
                        <li>Revoke their access to the admin panel immediately</li>
                        <li>Prevent them from performing any administrative actions</li>
                        <li>Require reactivation to restore their access</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
              <p className="text-sm text-gray-600">
                Are you sure you want to deactivate this admin user? This action can be reversed by reactivating the account later.
              </p>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowDeactivateConfirmation(false);
                  setAdminToDeactivate(null);
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Cancel
              </button>
              <button
                onClick={confirmDeactivate}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              >
                Deactivate User
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUserList;